import React from 'react'
import { PauseCircleFilled } from '@mui/icons-material'
import { Box, IconButton, Tooltip } from '@mui/material'
import HasPermission from '../../../../components/permissions/HasPermission'
import { StatusStyled } from '../../../../components/StatusStyled'
import {
  AdCopyStatus,
  AdGroupPerformanceStatsDocument,
  useUpdateAdCopyStatusMutation,
} from '../../../../__generated__/graphql'
import { AdCopyPerformanceStatsTableItem } from '../../../../interfaces/stats'
import { resolveStatusStyledColor } from '../../../../styles/theme/status'
import { useCanEditAdGroup } from '../../../../utils/hooks/adGroup/useCanEditAdGroup'
import { useAppTranslations } from '../../../../utils/hooks/useAppTranslations'
import { useSnackbarManager } from '../../../../utils/providers/snackbarContext'

const DISABLEABLE_STATUSES: AdCopyStatus[] = [AdCopyStatus.Active, AdCopyStatus.Processing]

const AdCopyStatsStatusCell: React.FC<{
  adCopyId: string
  status: AdCopyPerformanceStatsTableItem['status']
}> = ({ adCopyId, status }) => {
  const t = useAppTranslations()
  const canEditAdGroup = useCanEditAdGroup()
  const [, setSnackbar] = useSnackbarManager()
  const [updateAdCopyStatus, { loading }] = useUpdateAdCopyStatusMutation({
    refetchQueries: [AdGroupPerformanceStatsDocument],
    awaitRefetchQueries: true,
  })
  const canDisable = DISABLEABLE_STATUSES.includes(status as AdCopyStatus)
  const statusColor = resolveStatusStyledColor(AdCopyStatus.Inactive)

  const handleDisable = (): void => {
    updateAdCopyStatus({
      variables: {
        adCopyId,
        status: AdCopyStatus.Inactive,
      },
    })
      .then(() => setSnackbar({ severity: 'success', message: t('adcopy.updated') }))
      .catch((err: Error) => setSnackbar({ severity: 'error', message: err.message }))
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <StatusStyled status={status as AdCopyStatus} />
      {canDisable && canEditAdGroup && (
        <HasPermission roleRequired="edit" permissionRequired="adCopyValidation">
          <Tooltip title={t('commons.actions.disable') as string}>
            <IconButton
              data-testid="disableAdCopyButton"
              disabled={loading}
              onClick={handleDisable}
              size="small"
              sx={{
                color: statusColor,
                '& .MuiSvgIcon-root': {
                  color: statusColor,
                },
              }}
            >
              <PauseCircleFilled />
            </IconButton>
          </Tooltip>
        </HasPermission>
      )}
    </Box>
  )
}

export default AdCopyStatsStatusCell
